import React, { useState, useEffect } from 'react';
import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory } from '../utils/i18n';
import { 
  Zap, Activity, Coins, Sun, Cpu, Flame, Building, 
  Gauge, TrendingDown, Leaf, BatteryCharging, Wind, LineChart,
  Download, Copy, Check, ArrowLeft, Sparkles, 
  FileText, Upload, RefreshCw, FileCheck, X, Globe, ShieldCheck, AlertTriangle
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { autoDownloadReport } from '../utils/autoDownloader';
import TelegramExportButton from './TelegramExportButton';
import ExcelExportButton from './ExcelExportButton';
import { DigitalSignatureModal } from './DigitalSignatureModal';
import { getFieldLabel, getFieldValue, getNotesText } from '../utils/demoPresetsI18n';

interface EnergyAgentViewProps {
  agentId: string;
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const DEFAULT_ENERGY_DEMO: Record<string, {
  field1LabelUk: string; field1Value: string;
  field2LabelUk: string; field2Value: string;
  field3LabelUk: string; field3Value: string;
  field4LabelUk: string; field4Value: string;
  field5LabelUk: string; field5Value: string;
  field6LabelUk: string; field6Value: string;
  notesText: string;
}> = {
  'cbam-supply-chain-carbon-auditor': {
    field1LabelUk: "Експортер & Виробничий майданчик",
    field1Value: "ТОВ «Укр-Спец-Сталь», ЄДРПОУ 34891024, м. Запоріжжя (Україна)",
    field2LabelUk: "Номенклатура товарів CBAM & Код CN / УКТ ЗЕД",
    field2Value: "CN 7208 51 20 (Прокат плаский нелегований гарячекатаний товщиною понад 10 мм)",
    field3LabelUk: "Обсяг постачання до ЄС & Країна імпорту",
    field3Value: "Партія 4 200 тонн (Імпортер: Voestalpine Stahl AG, Австрія / Польща)",
    field4LabelUk: "Питомі вбудовані викиди Scope 1 + 2 (t CO2e / t продукції)",
    field4Value: "Scope 1: 1.64 t CO2e/t, Scope 2: 0.28 t CO2e/t (Сумарно: 1.92 t CO2e/t продукції)",
    field5LabelUk: "Верифікація ланцюга поставок & Дані субпостачальників",
    field5Value: "Верифіковані сертифікати ISO 14064-1 на залізорудну сировину та кокс від ГЗК (Scope 3 input)",
    field6LabelUk: "Форма звітності & Формат XML/XLSX Декларації",
    field6Value: "Офіційний CBAM Communication Template за Регламентом (EU) 2023/956 + Звіт Word (.docx)",
    notesText: "Провести верифікацію вбудованих прямих та непрямих викидів CO2 за ланцюгом постачання партії сталевого прокату обсягом 4200 тонн відповідно до Регламентів (EU) 2023/956 та 2023/1773. Розрахувати витрати імпортера на купівлю CBAM-сертифікатів за поточною ціною квот EU ETS (€68.5/t), підготувати розгорнутий аудиторський висновок за ДСТУ 4163:2020 та звітний файл для подання до CBAM Transitional Registry Єврокомісії."
  },
  'energy-cbam-carbon-tax-calculator': {
    field1LabelUk: "Вид продукції & Код УКТ ЗЕД / CN (Регламент ЄС 2023/956)",
    field1Value: "Гарячекатаний сталевий прокат у рулонах / Код 7208 38 00 00 (CN / TARIC)",
    field2LabelUk: "Обсяг річного експорту до ЄС & Країни імпорту",
    field2Value: "18 500 тонн / рік (Країни призначення: Польща, Німеччина, Італія)",
    field3LabelUk: "Прямі питомі викиди Scope 1 (t CO2e / t продукції)",
    field3Value: "1.82 t CO2e / t сталі (Доменне + конвертерне виробництво, природний газ + кокс)",
    field4LabelUk: "Непрямі питомі викиди Scope 2 (Електроенергія з мережі)",
    field4Value: "0.38 t CO2e / t сталі (Коефіцієнт викидів енергосистеми України: 0.42 кг CO2/кВт·год)",
    field5LabelUk: "Котирування квот EU ETS & Потенційне фінансове навантаження",
    field5Value: "Ціна квоти EU ETS: €68.50 / тонну CO2 (Оціночний CBAM збір: ~€2.78 млн/рік при повному запуску)",
    field6LabelUk: "Статус звітності у CBAM Transitional Registry",
    field6Value: "Квартальний звіт за Q2 2026 / Потрібен XML/XLSX звіт за формою Єврокомісії",
    notesText: "Здійснити повний розрахунок питомих прямих (Scope 1) та непрямих (Scope 2) вбудованих викидів парникових газів відповідно до імплементаційного регламенту ЄС CBAM (EU 2023/1773). Сформувати детальну калькуляцію за кожним джерелом викидів (паливо, електроди, феросплави, електроенергія), оцінити фінансові ризики для експортера при завершенні перехідного періоду та підготувати Квартальну декларацію CBAM у Word (.docx) та Excel за ДСТУ 4163:2020."
  },
  'energy-bess-net-billing-optimizer': {
    field1LabelUk: "Тип підприємства & Пікова приєднана потужність",
    field1Value: "Логістичний комплекс із холодильними терміналами / Приєднана потужність: 650 кВт",
    field2LabelUk: "Потужність генерації СЕС & Тип інверторів",
    field2Value: "Дахова СЕС 400 кВт (Мережеві сонячні інвертори Huawei SUN2000)",
    field3LabelUk: "Рекомендована конфігурація BESS (кВт / кВт·год)",
    field3Value: "Промисловий контейнер BESS 250 кВт / 520 кВт·год (LFP LiFePO4 800V C/2)",
    field4LabelUk: "Тарифний план на електроенергію & Графік АСКОЕ",
    field4Value: "РДН/ВДР + 3-зонний комерційний тариф (Денний пік 10:00-14:00: 9.80 грн/кВт·год, Ніч: 4.20 грн/кВт·год)",
    field5LabelUk: "Модель продажу надлишків (Net Billing / Самовиробництво)",
    field5Value: "Механізм самовиробництва Net Billing (Закон № 3220-IX) / Продаж надлишків постачальнику за ціною РДН",
    field6LabelUk: "Автономний резерв при аварійних відключеннях (блек-аут)",
    field6Value: "Забезпечення критичного навантаження холодильників 120 кВт протягом 4.5 годин автономно",
    notesText: "Провести математичне моделювання добового графіка споживання електроенергії за даними АСКОЕ. Розрахувати оптимальний алгоритм зрізання піків (Peak Shaving) за допомогою накопичувача BESS для уникнення штрафів ОСР. Змоделювати фінансові потоки та окупність у системі Net Billing при продажу літніх надлишків СЕС, розрахувати LCOE, NPV та строк повернення капітальних інвестицій (CAPEX BESS) у Word (.docx)."
  },
  'energy-auditor-certifier': {
    field1LabelUk: 'Тип та призначення будівлі', field1Value: 'Адміністративно-виробничий корпус ТОВ',
    field2LabelUk: 'Загальна площа (м²) & опалюваний об’єм (м³)', field2Value: '4 500 м² / 13 500 м³',
    field3LabelUk: 'Рік побудови та стан огороджувальних конструкцій', field3Value: '1985 р., цегла 510 мм без утеплення (R=0.72 m²K/W при нормі 3.3)',
    field4LabelUk: 'Поточне джерело опалення & тепловий вузол', field4Value: 'Централізоване теплопостачання (елеваторний вузол без ІТП)',
    field5LabelUk: 'Річні витрати тепла & вартість опалення', field5Value: '520 Гкал тепла / 1 250 000 грн/рік',
    field6LabelUk: 'Цільовий клас енергоефективності', field6Value: 'Підвищення з класу F до класу C/B (Зниження втрат тепла на 45%)',
    notesText: 'Необхідно провести розрахунок приведеного опору теплопередачі стін, вікон та покрівлі, сформувати Сертифікат енергетичної ефективності за стандартами Держенергоефективності та розробити інвестиційний план термомодернізації (утеплення 150 мм мінватою, заміна вікон, встановлення ІТП з погодозалежним автоматичним регулюванням) з розрахунком окупності (NPV, IRR, Payback).'
  },
  'iso-50001-energy-manager': {
    field1LabelUk: 'Галузь та специфіка виробництва', field1Value: 'Машинобудування та металообробне виробництво',
    field2LabelUk: 'Базовий період аналізу (EnB)', field2Value: '2025-2026 рр. (Річний цикл споживання)',
    field3LabelUk: 'Річне споживання електроенергії (кВт·год)', field3Value: '2 850 000 кВт·год / рік',
    field4LabelUk: 'Річне споживання газу (м³) & тепла (Гкал)', field4Value: '420 000 м³ газу / 120 Гкал тепла',
    field5LabelUk: 'Обсяг випуску готової продукції', field5Value: '15 200 тонн металовиробів / рік',
    field6LabelUk: 'Коефіцієнт потужності (cos φ) & графік роботи', field6Value: 'cos φ = 0.76 (потрібна компенсація), 2 зміни + нічний режим',
    notesText: 'Виявлено високе питоме споживання електроенергії у нічні години (18% від добового піку при зупиненому конвеєрі). Вимоги: побудувати базову лінію енергоспоживання EnB та показники EnPI відповідно до ISO 50001:2018, розрахувати економічний ефект від компенсації реактивної потужності (конденсаторна установка 240 кВАр) та переходу на 3-зонний тариф.'
  },
  'esco-green-investment-analyst': {
    field1LabelUk: 'Тип об’єкта та програма фінансування', field1Value: 'Комунальний заклад (Лікарня / Школа) / Програма «ГрінДІМ»',
    field2LabelUk: 'Базові річні витрати на енергоносії', field2Value: '1 800 000 грн / рік',
    field3LabelUk: 'Оціночний обсяг ЕСКО-інвестицій (CAPEX)', field3Value: '3 500 000 грн',
    field4LabelUk: 'Цільова економія енергоресурсів (%)', field4Value: '40% економії теплової енергії та електроенергії',
    field5LabelUk: 'Термін ЕСКО-контракту (років)', field5Value: '6 років (Energy Performance Contracting — EPC)',
    field6LabelUk: 'Розподіл заощаджених коштів (% ЕСКО / % Замовник)', field6Value: '80% ЕСКО-інвестор / 20% Замовник (після окупності 100% Замовник)',
    notesText: 'Модернізація освітлення (перехід на LED), заміна насосного обладнання та встановлення ІТП з диспетчеризацією. Необхідно провести фінансово-економічне моделювання кеш-флоу ЕСКО-проєкту, розрахувати NPV/IRR, оцінити можливість залучення гранту NEFCO/EBRD/USAID та підготувати проєкт ЕСКО-договору за ЗУ «Про запровадження нових інвестиційних можливостей...».'
  },
  'solar-wind-microgrid-designer': {
    field1LabelUk: 'Тип генерації та накопичувача BESS', field1Value: 'Дахова СЕС + Акумуляторна система BESS LiFePO4',
    field2LabelUk: 'Запланована потужність СЕС (кВт)', field2Value: '250 кВт (мережевий гібридний інвертор 250 кВт)',
    field3LabelUk: 'Ємність акумулятора BESS (кВт·год)', field3Value: '180 кВт·год (системний накопичувач 800V LiFePO4)',
    field4LabelUk: 'Річне споживання об’єкта & пікове навантаження', field4Value: '380 000 кВт·год / рік, денний пік 180 кВт',
    field5LabelUk: 'Режим роботи та мережевий тариф', field5Value: 'Net Billing (самовиробництво) + острівний режим при блек-аутах',
    field6LabelUk: 'Регіон розташування (інсоляція PVGIS)', field6Value: 'Одеська область (інсоляція 1250 кВт·год/м²/рік)',
    notesText: 'Проєктування дахової сонячної електростанції 250 кВт із накопичувачем 180 кВт·год для підприємства з графіком роботи 08:00-20:00. Розрахувати річну генерацію за даними PVGIS, частку покриття власного споживання (Self-consumption ratio), окупність в режимі Net Billing та тривалість автономної роботи (автаркії) при 4-годинних блек-аутах.'
  },
  'utility-smart-meter-analyst': {
    field1LabelUk: 'Тип мережі та облікового приладу', field1Value: 'Електромережі підприємства (АСКОЕ / ЛУЗОД)',
    field2LabelUk: 'Дозволена договорна потужність (кВт)', field2Value: '450 кВт (договір з ОСР)',
    field3LabelUk: 'Фактично зафіксований пік споживання (кВт)', field3Value: '580 кВт (перевищення дозволеного піку на 130 кВт)',
    field4LabelUk: 'Інтервал зрізів даних обліку', field4Value: '30-хвилинні зрізи потужності (1440 зрізів за місяць)',
    field5LabelUk: 'Період моніторингу та звітності', field5Value: 'Березень 2026 року',
    field6LabelUk: 'Основна інженерна проблема', field6Value: 'Штрафи ОСР за перевищення потужності & Peak Shaving',
    notesText: 'Аналіз АСКОЕ даних зафіксував систематичне перевищення дозволеної пікової потужності в інтервалі 11:00-13:00 (до 580 кВт при дозволених 450 кВт), що призводить до подвійної оплати за мажоритарним тарифом. Необхідно розробити алгоритм Peak Shaving (зрізання піків за рахунок накопичувача BESS та оптимізації включення потужних споживачів), а також перевірити витоки в індивідуальному тепловому пункті (ІТП).'
  }
};

const LOADING_STEPS_UK = [
  "Ініціалізація інженерного аналітичного модуля з Енергетики, ESG та CBAM...",
  "Аналіз наданих вхідних параметрів об'єкта, даних АСКОЕ/ЛУЗОД та файлів викидів...",
  "Проведення розрахунків питомих викидів Scope 1/2 (CBAM) та теплотехніки...",
  "Математичне моделювання BESS накопичувачів, Net Billing та окупності (NPV/IRR)...",
  "Формування підсумкового Енергетичного Паспорта та Звіту за ДСТУ 4163:2020..."
];

const LOADING_STEPS_PL = [
  "Inicjalizacja modułu analitycznego Energetyki, ESG i CBAM...",
  "Analiza parametrów wejściowych, profili 15-minutowych i emisji Scope 1/2...",
  "Kalkulacja podatku węglowego CBAM według Rozporządzenia UE 2023/956...",
  "Modelowanie doboru magazynu energii BESS, Peak Shaving i Net-billingu...",
  "Generowanie oficjalnego Raportu Energetyczno-Emisyjnego Word (.docx)..."
];

const LOADING_STEPS_EN = [
  "Initializing Energy, ESG, and CBAM Engineering analytics multi-agent engine...",
  "Processing load profiles, embedded GHG emissions math (Scope 1/2), and energy logs...",
  "Calculating CBAM carbon border tax adjustments per EU Regulation 2023/956...",
  "Simulating BESS battery storage capacity, peak shaving, and Net-Billing financial ROI...",
  "Generating final Energy & Carbon Audit statement in Word (.docx)..."
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Zap className={className} />;
  return <IconComponent className={className} />;
};

export default function EnergyAgentView({
  agentId,
  language,
  onBack,
  onGoHome,
  useEnterpriseContext,
  enterpriseName
}: EnergyAgentViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';

  const agentConfig = agentsConfig.find((a: any) => a.id === agentId) || {
    nameUk: 'Енергетичний & ESG Аналітичний Модуль',
    nameEn: 'Energy & ESG AI Agent',
    namePl: 'Moduł Energetyki i ESG AI',
    descriptionUk: 'Аудит енергоефективності, CBAM, BESS, Net Billing та ВДЕ.',
    descriptionEn: 'Energy efficiency, CBAM carbon tax, BESS battery storage and renewable analytics.',
    descriptionPl: 'Audyt efektywności energetycznej, CBAM, magazynów BESS i OZE.',
    icon: 'Zap',
    categoryUk: 'ЕНЕРГЕТИКА & ESG',
    categoryEn: 'ENERGY & ESG',
    categoryPl: 'ENERGETYKA & ESG'
  };

  const defaultDemo = DEFAULT_ENERGY_DEMO[agentId] || DEFAULT_ENERGY_DEMO['energy-cbam-carbon-tax-calculator'] || DEFAULT_ENERGY_DEMO['energy-auditor-certifier'];

  const [field1, setField1] = useState(getFieldValue(defaultDemo, 1, language));
  const [field2, setField2] = useState(getFieldValue(defaultDemo, 2, language));
  const [field3, setField3] = useState(getFieldValue(defaultDemo, 3, language));
  const [field4, setField4] = useState(getFieldValue(defaultDemo, 4, language));
  const [field5, setField5] = useState(getFieldValue(defaultDemo, 5, language));
  const [field6, setField6] = useState(getFieldValue(defaultDemo, 6, language));
  const [notesText, setNotesText] = useState(getNotesText(defaultDemo, language));

  const [attachedFiles, setAttachedFiles] = useState<Array<{ name: string; size: number; text: string }>>([]);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [reportResult, setReportResult] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);

  useEffect(() => {
    setField1(getFieldValue(defaultDemo, 1, language));
    setField2(getFieldValue(defaultDemo, 2, language));
    setField3(getFieldValue(defaultDemo, 3, language));
    setField4(getFieldValue(defaultDemo, 4, language));
    setField5(getFieldValue(defaultDemo, 5, language));
    setField6(getFieldValue(defaultDemo, 6, language));
    setNotesText(getNotesText(defaultDemo, language));
    setReportResult(null);
  }, [defaultDemo, language]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isProcessing) {
      setLoadingStepIndex(0);
      const steps = isPl ? LOADING_STEPS_PL : isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex(prev => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing, isUk, isPl]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsReadingFile(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch('/api/extract-text', { method: 'POST', body: formData });

        if (res.ok) {
          const data = await res.json();
          setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: data.text || '' }]);
        } else {
          setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, text: `[Файл ${file.name} додано]` }]);
        }
      }
    } catch (err) {
      console.error('File upload error:', err);
    } finally {
      setIsReadingFile(false);
    }
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRunAnalysis = async () => {
    setIsProcessing(true);
    setReportResult(null);
    setErrorMessage(null);
    setLoadingStepIndex(0);

    try {
      const combinedDocText = attachedFiles.map(f => `--- ФАЙЛ: ${f.name} ---\n${f.text}`).join('\n\n');
      
      const inputPrompt = `
[ЗАВДАННЯ ТА ВИХІДНІ ДАНІ ЕНЕРГОАУДИТУ / CBAM / BESS]:
${notesText}

[ВХІДНІ ІНЖЕНЕРНІ ТА ЕМІСІЙНІ ПАРАМЕТРИ]:
- ${defaultDemo.field1LabelUk}: ${field1}
- ${defaultDemo.field2LabelUk}: ${field2}
- ${defaultDemo.field3LabelUk}: ${field3}
- ${defaultDemo.field4LabelUk}: ${field4}
- ${defaultDemo.field5LabelUk}: ${field5}
- ${defaultDemo.field6LabelUk}: ${field6}

${combinedDocText ? `[ДОДАНІ ЗВІТИ АСКОЕ / ЗРІЗИ / РЕЄСТРИ ВИКИДІВ]:\n${combinedDocText}` : ''}
`;

      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: agentConfig.nameUk || "Калькулятор Вуглецевого Коригування CBAM ($CO_2$ для Експорту в ЄС)",
          agentId: agentId,
          inputs: {
            task_description: inputPrompt,
            agent_name: agentConfig.nameUk,
            target_language: isPl ? 'pl' : isUk ? 'uk' : 'en'
          },
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isPl ? 'Błąd wykonania analizy energetycznej/CBAM.' : isUk ? 'Помилка виконання енергетичного аналізу/CBAM.' : 'Execution failed.'));
      }

      const data = await response.json();

      if (data.jobId) {
        const pollJob = async () => {
          try {
            const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
            if (!statusRes.ok) {
              throw new Error(isPl ? 'Błąd sprawdzania statusu zadania.' : isUk ? 'Помилка перевірки статусу завдання.' : 'Status check failed.');
            }
            const statusData = await statusRes.json();
            if (statusData.status === 'completed') {
              const rawReport = statusData.report || statusData.result || '';
              const resText = typeof rawReport === 'string'
                ? rawReport
                : (typeof rawReport === 'object' && rawReport !== null
                    ? (rawReport.report || rawReport.result || JSON.stringify(rawReport, null, 2))
                    : String(rawReport));

              if (!resText) {
                throw new Error(isPl ? 'Serwer zwrócił pusty raport.' : isUk ? 'Сервер повернув порожній результат звіту.' : 'Server returned empty report.');
              }
              setReportResult(resText);
              setIsProcessing(false);
            } else if (statusData.status === 'failed') {
              setErrorMessage(statusData.error || (isPl ? 'Błąd generowania raportu.' : isUk ? 'Помилка генерації звіту.' : 'Report generation failed.'));
              setIsProcessing(false);
            } else {
              setTimeout(pollJob, 2500);
            }
          } catch (pollErr: any) {
            console.error('Polling error:', pollErr);
            setErrorMessage(pollErr.message || (isPl ? 'Wystąpił błąd podczas pobierania wyników.' : isUk ? 'Сталася помилка при отриманні результатів.' : 'Polling error occurred.'));
            setIsProcessing(false);
          }
        };
        pollJob();
      } else if (data.report) {
        setReportResult(data.report);
        setIsProcessing(false);
      } else {
        throw new Error(isPl ? 'Nieoczekiwana odpowiedź serwera.' : isUk ? 'Неочікувана відповідь сервера.' : 'Unexpected server response.');
      }
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err.message || (isPl ? 'Wystąpił błąd podczas analizy.' : isUk ? 'Сталася помилка під час аналізу.' : 'An error occurred.'));
      setIsProcessing(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!reportResult) return;
    try {
      const name = (agentConfig.nameUk || "Energy_CBAM_Report").replace(/[^a-zA-Z0-9А-Яа-яЄєІіЇїҐґ_-]/g, "_");
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: reportResult, fileName: name })
      });
      if (!response.ok) throw new Error(isPl ? 'Nie udało się pobrać Word pliku.' : isUk ? 'Не вдалося згенерувати Word файл.' : 'Failed to generate Word.');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message);
    }
  };

  const applyPresetScenario = (scenario: 'ua' | 'pl' | 'eu') => {
    if (scenario === 'ua') {
      setField1("Гарячекатаний сталевий прокат у рулонах / Код УКТ ЗЕД 7208 38 00 00");
      setField2("18 500 тонн / рік експорту до країн ЄС (Польща, Італія, Німеччина)");
      setField3("1.82 t CO2e / t сталі (Прямі викиди Scope 1, доменне виробництво)");
      setField4("0.38 t CO2e / t сталі (Непрямі викиди Scope 2 з української енергомережі)");
      setField5("Ціна квоти EU ETS: €68.50 / т CO2 (Оціночне навантаження ~€2.78 млн/рік)");
      setField6("Квартальний звіт CBAM Transitional Registry Q2 2026 / Методологія EU 2023/1773");
      setNotesText("Здійснити повний розрахунок питомих прямих (Scope 1) та непрямих (Scope 2) вбудованих викидів парникових газів відповідно до імплементаційного регламенту ЄС CBAM (EU 2023/1773). Сформувати детальну калькуляцію за кожним джерелом викидів (паливо, електроди, феросплави, електроенергія), оцінити фінансові ризики для експортера при завершенні перехідного періоду та підготувати Квартальну декларацію CBAM у Word (.docx) та Excel за ДСТУ 4163:2020.");
    } else if (scenario === 'pl') {
      setField1("Wyroby walcowane ze stali niestopowej / Kod CN 7214 20 00 / Huta stali w Polsce");
      setField2("24 000 ton / rok dostaw na rynek wewnętrzny UE oraz reeksport poza UE");
      setField3("Emisje bezpośrednie Scope 1: 0.65 t CO2e / t wyrobów (Elektryczny piec łukowy EAF)");
      setField4("Emisje pośrednie Scope 2: 0.58 t CO2e / t (Polski miks energetyczny KOBiZE)");
      setField5("Cena uprawnień EU ETS: 72,00 EUR/t CO2 / Audyt dekarbonizacji ISO 14064");
      setField6("Raport do Krajowego Ośrodka Bilansowania i Zarządzania Emisjami (KOBiZE) + Deklaracja CBAM");
      setNotesText("Przeprowadzić kalkulację śladu węglowego wyrobów stalowych produkowanych w technologii EAF (piec łukowy) zgodnie z normą ISO 14064 i wymogami unijnego mechanizmu CBAM. Oszacować korzyści z zakupu energii z OZE (umowy cPPA) na obniżenie wskaźnika Scope 2 oraz przygotować deklarację emisyjną dla unijnego rejestru przejściowego CBAM.");
    } else if (scenario === 'eu') {
      setField1("EU CBAM Compliance Declaration for Fertilizers (Anhydrous Ammonia / CN 2814 10 00)");
      setField2("60,000 tonnes / annum imported into Rotterdam from non-EU manufacturing plant");
      setField3("Embedded Scope 1 Emissions: 2.15 t CO2e / t Ammonia (Steam Methane Reforming SMR)");
      setField4("Embedded Scope 2 Emissions: 0.22 t CO2e / t Ammonia (Industrial grid power supply)");
      setField5("EU ETS Benchmark: €70.50 / tonne CO2e / Total Annual Carbon Liability: €10,039,200");
      setField6("EU CBAM Transitional Registry XML/XLSX template compliance verification & Defect Audit");
      setNotesText("Audit fertilizer import supply chain under EU CBAM Regulation 2023/956. Calculate specific embedded direct and indirect emissions per tonne of ammonia product. Verify default vs actual GHG emission factors, model financial carbon surcharge exposure under EU ETS benchmark pricing, and export verified XML/Word declaration dossier.");
    }
  };

  return (
    <div className="space-y-8 animate-fade-in pb-16 font-mono max-w-5xl mx-auto px-4">
      {/* Top Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <div className="flex items-center gap-3">
          <button
            onClick={onGoHome || onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            🏠 {isPl ? 'Strona Główna' : isUk ? 'На головну' : 'Home'}
          </button>
          <button
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
          >
            <ArrowLeft className="w-4 h-4 text-sky-400" />
            <span>{isPl ? '← Powrót do Energetyki' : isUk ? '← До Центру Енергетики' : '← Back to Energy Hub'}</span>
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md flex items-center gap-1">
            <Globe className="w-3.5 h-3.5 text-emerald-400" />
            <span>CBAM EU 2023/956 • BESS • NET BILLING</span>
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-amber-500/20 text-amber-300 border border-amber-400/30 rounded-md">
            ISO 50001 • ЗУ № 3220-IX • EU ETS
          </span>
        </div>
      </div>

      {useEnterpriseContext && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3.5 flex items-center justify-between text-xs text-amber-800">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
            <div>
              <span className="font-bold">{isPl ? "Aktywny kontekst firmy: " : isUk ? "Активовано контекст підприємства: " : "Enterprise Context Active: "}</span>
              <span className="font-mono">{enterpriseName || (isPl ? "Baza wiedzy przedsiębiorstwa" : isUk ? "База знань компанії" : "Company Knowledge Base")}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 rounded text-amber-700 font-bold uppercase tracking-wider">
            {isPl ? "Corporate DNA" : isUk ? "База знань" : "Knowledge Base"}
          </span>
        </div>
      )}

      {/* Hero Banner */}
      <div className="bg-slate-950 border border-emerald-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl space-y-4">
        <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
          <div className="p-4 bg-emerald-950/80 border border-emerald-500/40 rounded-2xl text-emerald-400 shrink-0 shadow-lg">
            <AgentIcon name={agentConfig.icon} className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/30 text-emerald-200 border border-emerald-400/40 rounded">
                {getLocalizedAgentCategory(agentConfig, language)}
              </span>
              <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-sky-500/30 text-sky-200 border border-sky-400/40 rounded flex items-center gap-1">
                <ShieldCheck className="w-3 h-3 text-sky-400" />
                <span>100% Енергетичний & ESG Комплаєнс</span>
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-extrabold uppercase tracking-wide text-white">
              {getLocalizedAgentName(agentConfig, language)}
            </h1>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
              {getLocalizedAgentDesc(agentConfig, language)}
            </p>
          </div>
        </div>
      </div>

      {/* 1-Click Fast Presets */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-white space-y-3">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-extrabold uppercase tracking-wide flex items-center gap-1.5 text-amber-400">
            <Sparkles className="w-4 h-4 text-amber-400 animate-pulse" />
            <span>{isPl ? "⚡ Gotowe scenariusze energetyczne & ESG (1 kliknięcie):" : isUk ? "⚡ Готові сценарії енергетики та ESG (1 клік):" : "⚡ Ready Energy & ESG Scenarios (1-Click Fill):"}</span>
          </span>
          <span className="text-[10px] text-slate-400 font-mono">CBAM • BESS • Net Billing • EU ETS</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => applyPresetScenario('ua')}
            className="p-3 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-emerald-500/50 rounded-xl text-left transition-all cursor-pointer group"
          >
            <div className="text-xs font-bold text-emerald-300 group-hover:text-emerald-200 flex items-center gap-1.5">
              <span>🇺🇦 Експортний CBAM звіт (Україна)</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-1 font-mono">Сталь • Scope 1/2 • Регламент 2023/956</div>
          </button>

          <button
            type="button"
            onClick={() => applyPresetScenario('pl')}
            className="p-3 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-blue-500/50 rounded-xl text-left transition-all cursor-pointer group"
          >
            <div className="text-xs font-bold text-blue-300 group-hover:text-blue-200 flex items-center gap-1.5">
              <span>🇵🇱 Dekarbonizacja & KOBiZE (Polska)</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-1 font-mono">Huta EAF • ISO 14064 • EU ETS • cPPA</div>
          </button>

          <button
            type="button"
            onClick={() => applyPresetScenario('eu')}
            className="p-3 bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-purple-500/50 rounded-xl text-left transition-all cursor-pointer group"
          >
            <div className="text-xs font-bold text-purple-300 group-hover:text-purple-200 flex items-center gap-1.5">
              <span>🇪🇺 EU CBAM Ammonia Audit (Rotterdam)</span>
            </div>
            <div className="text-[10px] text-slate-400 mt-1 font-mono">Fertilizers • SMR Scope 1 • EU Registry</div>
          </button>
        </div>
      </div>

      {/* Main Parameters Form */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-6 text-white shadow-xl">
        <h3 className="text-sm font-extrabold uppercase tracking-wider text-emerald-300 flex items-center gap-2 border-b border-slate-800 pb-3">
          <Zap className="w-4 h-4 text-emerald-400" />
          <span>{isPl ? 'PARAMETRY ENERGETYCZNE, EMISYJNE I BAZY DANYCH:' : isUk ? 'ВХІДНІ ПАРАМЕТРИ ЕНЕРГЕТИКИ, ВИКИДІВ CO2 ТА ОБЛАДНАННЯ:' : 'ENERGY, EMISSIONS & BESS PARAMETERS:'}</span>
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">1. {getFieldLabel(defaultDemo, 1, language)}</label>
            <input
              type="text"
              value={field1}
              onChange={e => setField1(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-emerald-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">2. {getFieldLabel(defaultDemo, 2, language)}</label>
            <input
              type="text"
              value={field2}
              onChange={e => setField2(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-emerald-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">3. {getFieldLabel(defaultDemo, 3, language)}</label>
            <input
              type="text"
              value={field3}
              onChange={e => setField3(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-emerald-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">4. {getFieldLabel(defaultDemo, 4, language)}</label>
            <input
              type="text"
              value={field4}
              onChange={e => setField4(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-emerald-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">5. {getFieldLabel(defaultDemo, 5, language)}</label>
            <input
              type="text"
              value={field5}
              onChange={e => setField5(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-emerald-500 focus:outline-none font-mono"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">6. {getFieldLabel(defaultDemo, 6, language)}</label>
            <input
              type="text"
              value={field6}
              onChange={e => setField6(e.target.value)}
              placeholder={isPl ? "Wpisz lub wybierz..." : isUk ? "Введіть або оберіть..." : "Enter or select..."}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-emerald-500 focus:outline-none font-mono"
            />
          </div>
        </div>

        <div className="space-y-2 pt-2">
          <label className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">
            {isPl ? '7. ZAKRES MODELOWANIA I WYMAGANIA AUDYTU ENERGETYCZNEGO/CBAM:' : isUk ? '7. ДЕТАЛЬНИЙ ОПИС ВИРОБНИЦТВА, ЗРІЗІВ АСКОЕ ТА ЗАВДАННЯ АУДИТУ:' : '7. AUDIT INSTRUCTIONS & ENERGY SIMULATION REQUIREMENTS:'}
          </label>
          <textarea 
            rows={5} 
            value={notesText} 
            onChange={e => setNotesText(e.target.value)} 
            className="w-full p-3.5 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:border-emerald-500 focus:outline-none font-mono leading-relaxed" 
          />
        </div>

        {/* File Attachments */}
        <div className="space-y-3 pt-4 border-t border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-300 uppercase tracking-wide flex items-center gap-2">
              <Upload className="w-4 h-4 text-emerald-400" />
              <span>{isPl ? 'ZAŁĄCZNIKI (ZBIORY DANYCH 15-MIN, EMISJE, FAKTURY, PDF / XLSX):' : isUk ? 'СУПРОВІДНІ ФАЙЛИ ЗРІЗІВ АСКОЕ, РЕЄСТРІВ ВИКИДІВ CO2 (PDF / DOCX / XLSX):' : 'SUPPORTING AMR/AMI LOGS & GHG EMISSIONS SPREADSHEETS:'}</span>
            </span>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <label className="px-4 py-2.5 bg-emerald-950/80 hover:bg-emerald-900 border border-emerald-500/40 text-emerald-200 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center gap-2">
              <Upload className="w-4 h-4 text-emerald-400" />
              <span>{isReadingFile ? (isPl ? 'Odczytywanie...' : 'Зчитування...') : (isPl ? '➕ Dodaj pliki danych' : '➕ Завантажити дані')}</span>
              <input type="file" multiple accept=".pdf,.docx,.xlsx,.csv,.json,.txt" onChange={handleFileUpload} className="hidden" />
            </label>

            {attachedFiles.map((file, idx) => (
              <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-slate-200">
                <FileCheck className="w-3.5 h-3.5 text-emerald-400" />
                <span className="font-mono text-[11px] max-w-[150px] truncate">{file.name}</span>
                <button onClick={() => removeFile(idx)} className="text-slate-400 hover:text-red-400 p-0.5">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {errorMessage && (
          <div className="p-3.5 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-300 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Action Button */}
        <div className="pt-4 border-t border-slate-800 flex justify-end">
          <button
            onClick={handleRunAnalysis}
            disabled={isProcessing || !notesText.trim()}
            className="w-full sm:w-auto px-8 py-3.5 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 hover:scale-[1.01]"
          >
            {isProcessing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin text-white" />
                <span>{isPl ? LOADING_STEPS_PL[loadingStepIndex] : isUk ? LOADING_STEPS_UK[loadingStepIndex] : LOADING_STEPS_EN[loadingStepIndex]}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span>{isPl ? '🚀 ROZPOCZNIJ AUDYT ENERGETYCZNY & CBAM' : isUk ? '🚀 ЗАПУСТИТИ ЕНЕРГЕТИЧНИЙ АУДИТ & CBAM' : '🚀 RUN ENERGY & CBAM AUDIT'}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Result Section */}
      {reportResult && (
        <div className="bg-slate-950 border border-emerald-500/40 rounded-2xl p-6 sm:p-8 space-y-4 text-white shadow-2xl animate-fade-in">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3 flex-wrap gap-2">
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-emerald-300 flex items-center gap-2">
              <FileText className="w-4 h-4 text-emerald-400" />
              <span>{isPl ? 'OFICJALNY RAPORT ENERGETYCZNO-EMISYJNY & CBAM:' : isUk ? 'ОФІЦІЙНИЙ ЕНЕРГЕТИЧНИЙ ПАСПОРТ & ЗВІТ CBAM / BESS:' : 'OFFICIAL ENERGY & CBAM AUDIT FINDINGS:'}</span>
            </h3>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleDownloadWord}
                className="px-3 py-1.5 bg-emerald-950 hover:bg-emerald-900 border border-emerald-500/40 text-emerald-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <Download className="w-3.5 h-3.5 text-emerald-400" />
                <span>Word (.docx)</span>
              </button>

              <ExcelExportButton
                reportText={reportResult}
                fileName={`${agentConfig.nameUk || 'Energy_CBAM'}_data`}
                className="px-3 py-1.5 bg-teal-950 hover:bg-teal-900 border border-teal-500/40 text-teal-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              />

              <TelegramExportButton
                reportText={reportResult}
                agentName={getLocalizedAgentName(agentConfig, language)}
              />

              <button
                onClick={() => setShowSignModal(true)}
                className="px-3 py-1.5 bg-amber-950 hover:bg-amber-900 border border-amber-500/40 text-amber-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
                <span>{isPl ? '✍️ Podpisz QES' : isUk ? '✍️ Підписати КЕП' : '✍️ Sign QES'}</span>
              </button>

              <button
                onClick={() => {
                  navigator.clipboard.writeText(reportResult);
                  setIsCopied(true);
                  setTimeout(() => setIsCopied(false), 2000);
                }}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 text-slate-300" />}
                <span>{isCopied ? (isPl ? 'Skopiowano!' : isUk ? 'Скопійовано!' : 'Copied!') : (isPl ? 'Kopiuj' : isUk ? 'Копіювати' : 'Copy')}</span>
              </button>
            </div>
          </div>

          <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-5 text-xs font-mono leading-relaxed text-slate-200 whitespace-pre-wrap max-h-[650px] overflow-y-auto">
            {reportResult}
          </div>
        </div>
      )}

      {showSignModal && reportResult && (
        <DigitalSignatureModal
          isOpen={showSignModal}
          onClose={() => setShowSignModal(false)}
          documentText={reportResult}
          documentTitle={getLocalizedAgentName(agentConfig, language)}
          language={language}
        />
      )}
    </div>
  );
}
